// tslint:disable:naming-convention
import { HtmlEscaper } from './html-escaper.utility';
import * as Utilities from './pipeline.utility';
import { RegExpEscaper } from './reg-exp-escaper.utility';

/**
 * This class is designed to take an array of path elements and properly format them for display.
 */
export class PathArrayFormatter {
    /**
     * This controls the length that the formatter will truncate its result to.
     */
    public static DefaultMaxLength = 60;

    /**
     * This is the separator that appears between adjacent elements of the path in ltr.
     */
    private static readonly DefaultPathSepLtr = ' » ';

    /**
     * This is the separator that appears between adjacent elements of the path in rtl.
     */
    private static readonly DefaultPathSepRtl = ' « ';

    /**
     * This is the element that will be shown to indicate that the display was truncated.
     */
    private static readonly Ellipsis = '…';

    /**
     * Path segment element opening tag
     */
    private static readonly PathSegmentPrefix = '<span dir="auto">';

    /**
     * Path segment element closing tag
     */
    private static readonly PathSegmentPostfix = '</span>';

    /**
     * This is the pipeline that path components will travel through to produce the correct display result.
     */
    private static readonly FormatterPipeline = new Utilities.Pipeline(
        PathArrayFormatter.ZeroElements,
        PathArrayFormatter.TopTooLong,
        PathArrayFormatter.SingleElement,
        PathArrayFormatter.TopTooLongForSingleFollower,
        PathArrayFormatter.TopTooLongForMultipleFollowers,
        PathArrayFormatter.TopAndBottomTooLongAndOnlyElements,
        PathArrayFormatter.TopAndBottomTooLongAndOtherElements,
        PathArrayFormatter.MultipleElements,
        PathArrayFormatter.CollapseFirst,
        PathArrayFormatter.CollapseRemaining);

    private static readonly RegExpCache = new Map<string, RegExp>();

    /**
     * Formats an array of path components in the form a » b » c, truncated to the max length
     * @return {string} - The path as HTML or plaintext.
     */
    public static Format(pathArray: Array<string>, rtl: boolean, maxLength?: number): Array<string> {
        return PathArrayFormatter.FormatWithSeparator(pathArray, PathArrayFormatter.DefaultSeparator(rtl), maxLength);
    }

    /**
     * Formats an array of path components using a custom path separator, truncated to the max length
     * @return {Array<string>} - The path as plaintext in index 0 and html in index 1.
     */
    private static FormatWithSeparator(pathArray: Array<string>, pathSeparator: string, maxLength?: number): Array<string> {
        const array = [];
        const length = maxLength || PathArrayFormatter.DefaultMaxLength;
        const plainTextPath = PathArrayFormatter.FormatterPipeline.Run({ PathArray: pathArray, Formatted: '', Separator: pathSeparator, MaxLength: length}).Formatted;
        array.push(plainTextPath);

        //Enclose each path segment and separator in a separate directional context so that rtl paths will be ordered correctly
        const escapedSeparator = HtmlEscaper.escapeHtml(pathSeparator);
        const htmlSeparator = `${PathArrayFormatter.PathSegmentPostfix}${PathArrayFormatter.PathSegmentPrefix}${escapedSeparator}${PathArrayFormatter.PathSegmentPostfix}${PathArrayFormatter.PathSegmentPrefix}`;
        const separatorRegex = PathArrayFormatter.GetRegExp(RegExpEscaper.escape(escapedSeparator), 'g');
        array.push(`${PathArrayFormatter.PathSegmentPrefix}${HtmlEscaper.escapeHtml(plainTextPath).replace(separatorRegex, htmlSeparator)}${PathArrayFormatter.PathSegmentPostfix}`);
        return array;
    }

    private static DefaultSeparator(isRtl: boolean) {
        return isRtl ? PathArrayFormatter.DefaultPathSepRtl : PathArrayFormatter.DefaultPathSepLtr;
    }

    /**
     * Handles the case when the path array does not contain any elements.
     *
     * @param {FormatterState} state - The current state of the pipeline.
     * @return {PipelineResult<FormatterState>} - The updated state of the pipeline.
     */
    private static ZeroElements(state: FormatterState) {
        if (!state.PathArray || state.PathArray.length === 0) {
            state.Formatted = '';

            return Utilities.PipelineResult.Finished(state);
        }

        return Utilities.PipelineResult.Unfinished(state);
    }

    /**
     * Handles the case when the first element is so long that it needs to be truncated.
     *
     * @param {FormatterState} state - The current state of the pipeline.
     * @return {PipelineResult<FormatterState>} - The updated state of the pipeline.
     */
    private static TopTooLong(state: FormatterState) {
        const top = state.PathArray[0].trim();

        if (top.length > state.MaxLength) {
            state.Formatted = top.substr(0, state.MaxLength - 1).trim() + PathArrayFormatter.Ellipsis;

            return Utilities.PipelineResult.Finished(state);
        }

        return Utilities.PipelineResult.Unfinished(state);
    }

    /**
     * Handles the case when the path array only contains one element.
     *
     * @param {FormatterState} state - The current state of the pipeline.
     * @return {PipelineResult<FormatterState>} - The updated state of the pipeline.
     */
    private static SingleElement(state: FormatterState) {
        if (state.PathArray.length === 1) {
            state.Formatted = state.PathArray[0].trim();

            return Utilities.PipelineResult.Finished(state);
        }

        return Utilities.PipelineResult.Unfinished(state);
    }

    /**
     * Handles the case when there's not enough room to display the path separator after the first element.
     *
     * @param {FormatterState} state - The current state of the pipeline.
     * @return {PipelineResult<FormatterState>} - The updated state of the pipeline.
     */
    private static TopTooLongForSingleFollower(state: FormatterState) {
        const top = state.PathArray[0].trim();

        if (top.length + state.Separator.length + PathArrayFormatter.Ellipsis.length > state.MaxLength) {
            state.Formatted = top;

            return Utilities.PipelineResult.Finished(state);
        }

        return Utilities.PipelineResult.Unfinished(state);
    }

    /**
     * Handles the case when there are more than two elements, but there's not enough room to display two path separators.
     *
     * @param {FormatterState} state - The current state of the pipeline.
     * @return {PipelineResult<FormatterState>} - The updated state of the pipeline.
     */
    private static TopTooLongForMultipleFollowers(state: FormatterState) {
        const top = state.PathArray[0].trim();

        if (state.PathArray.length > 2 &&
            top.length + (state.Separator.length + PathArrayFormatter.Ellipsis.length) * 2 > state.MaxLength) {
            state.Formatted = top;

            return Utilities.PipelineResult.Finished(state);
        }

        return Utilities.PipelineResult.Unfinished(state);
    }

    /**
     * Handles the case when there are two elements, and the second one needs to be truncated.
     *
     * @param {FormatterState} state - The current state of the pipeline.
     * @return {PipelineResult<FormatterState>} - The updated state of the pipeline.
     */
    private static TopAndBottomTooLongAndOnlyElements(state: FormatterState) {
        if (state.PathArray.length === 2) {
            const top = state.PathArray[0].trim();
            const bottom = state.PathArray[1].trim();
            const bottomStart = top.length + bottom.length + state.Separator.length + 1 - state.MaxLength;

            // bottomStart === 1 would replace the first character with an ellipsis which wouldn't help with the length
            if (bottomStart > 1) {
                state.Formatted = PathArrayFormatter.Join(top,
                    PathArrayFormatter.Ellipsis + bottom.substr(bottomStart).trim(), state.Separator);

                return Utilities.PipelineResult.Finished(state);
            }
        }

        return Utilities.PipelineResult.Unfinished(state);
    }

    /**
     * Handles the case when there are more than two elements, and the last one needs to be truncated.
     *
     * @param {FormatterState} state - The current state of the pipeline.
     * @return {PipelineResult<FormatterState>} - The updated state of the pipeline.
     */
    private static TopAndBottomTooLongAndOtherElements(state: FormatterState) {
        if (state.PathArray.length > 2) {
            const top = state.PathArray[0].trim();
            let bottom = state.PathArray[state.PathArray.length - 1].trim();
            const bottomStart = top.length + bottom.length + 2 * (state.Separator.length + 1) - state.MaxLength;

            // bottomStart === 1 would replace the first character with an ellipsis which wouldn't help with the length
            if (bottomStart > 1) {
                bottom = bottom.substr(bottomStart).trim();
                state.Formatted = PathArrayFormatter.Join(top, PathArrayFormatter.Ellipsis, state.Separator);

                // only display the bottom if there is at least one character after the ellipsis
                if (bottom.length > 0) {
                    state.Formatted = PathArrayFormatter.Join(state.Formatted,
                        PathArrayFormatter.Ellipsis + bottom, state.Separator);
                }

                return Utilities.PipelineResult.Finished(state);
            }
        }

        return Utilities.PipelineResult.Unfinished(state);
    }

    /**
     * Handles the normal case of putting a separator between each adjacent element.
     *
     * @param {FormatterState} state - The current state of the pipeline.
     * @return {PipelineResult<FormatterState>} - The updated state of the pipeline.
     */
    private static MultipleElements(state: FormatterState) {
        state.Formatted = state.PathArray.reduce((first, second) => PathArrayFormatter.Join(first, second, state.Separator));

        if (state.Formatted.length <= state.MaxLength) {
            return Utilities.PipelineResult.Finished(state);
        }

        return Utilities.PipelineResult.Unfinished(state);
    }

    /**
     * Handles the case when an interior path component needs to be omitted.
     *
     * @param {FormatterState} state - The current state of the pipeline.
     * @return {PipelineResult<FormatterState>} - The updated state of the pipeline.
     */
    private static CollapseFirst(state: FormatterState) {
        const pattern = state.Separator + '.*?' + state.Separator;

        const reg = PathArrayFormatter.GetRegExp(pattern);

        state.Formatted = state.Formatted.replace(reg,
            state.Separator + PathArrayFormatter.Ellipsis + state.Separator);

        if (state.Formatted.length <= state.MaxLength) {
            return Utilities.PipelineResult.Finished(state);
        }

        return Utilities.PipelineResult.Unfinished(state);
    }

    /**
     * Handles the case when multiple interior path components need to be omitted.
     *
     * @param {FormatterState} state - The current state of the pipeline.
     * @return {PipelineResult<FormatterState>} - The updated state of the pipeline.
     */
    private static CollapseRemaining(state: FormatterState) {
        const pattern = state.Separator + '.*?' + state.Separator + '.*?' + state.Separator;

        const reg = PathArrayFormatter.GetRegExp(pattern);

        while (state.Formatted.length > state.MaxLength) {
            const previousFormatted = state.Formatted;
            state.Formatted = state.Formatted.replace(reg,
                state.Separator + PathArrayFormatter.Ellipsis + state.Separator);
            if (previousFormatted === state.Formatted) {
                break;
            }
        }

        return Utilities.PipelineResult.Finished(state);
    }

    /**
     * Joins two strings together with the path separator in between them.
     *
     * @param {string} left - The string that will be on the left.
     * @param {string} right - The string that will be on the right.
     * @return {string} - The joined string.
    */
    private static Join(left: string, right: string, separator: string) {
        if (right.length === 0) {
            return left;
        }

        return left.trim() + separator + right.trim();
    }

    private static GetRegExp(pattern: string, flags: string = ''): RegExp {
        const key = `${pattern}/${flags}`;

        if (PathArrayFormatter.RegExpCache.has(key)) {
            return PathArrayFormatter.RegExpCache.get(key);
        }

        const reg = new RegExp(pattern, flags);
        PathArrayFormatter.RegExpCache.set(key, reg);
        return reg;
    }
}

/**
 * The interface that will be used for the formatting pipeline
*/
interface FormatterState {
    /**
     * The path array. This will never be modified.
    */
    PathArray: Array<string>;

    /**
     * The current formatted string.
    */
    Formatted: string;

    /**
     * The string separator to use between items in the path.
    */
    Separator: string;

    /**
     * The max length the formatted string can be.
    */
    MaxLength: number;
}
